import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import Loading from "../common/Loading";
import { ACCENT, PageShell, PrimaryButton, SecondaryButton } from "./shared";
import { MESSAGES, formatBytes, trackApi, trackErrorMessage } from "./trackApi";

// Moderator screen: audio replacements waiting for review. Only admin/moderator accounts are allowed by the server;
// anyone else sees the server's refusal.
const formatDate = (v) => {
  const d = v ? new Date(v) : null;
  return d && !Number.isNaN(d.getTime()) ? d.toLocaleString() : "—";
};
const seconds = (v) => (Number.isFinite(Number(v)) ? `${Math.floor(Number(v) / 60)}:${String(Math.round(Number(v) % 60)).padStart(2, "0")}` : "—");

function Row({ item, onDone }) {
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const review = async (decision) => {
    if (busy) return;
    if (decision === "reject" && !reason.trim()) {
      setError("Please enter a reason for the artist.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      await trackApi.reviewAudioReplacement({ track_id: item.track_id, decision, ...(decision === "reject" ? { reason: reason.trim() } : {}) });
      toast.success(decision === "approve" ? "Replacement approved." : "Replacement rejected.");
      onDone();
    } catch (e) {
      setError(trackErrorMessage(e, MESSAGES[500]));
      setBusy(false);
    }
  };

  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-5" data-testid="replacement-row">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-lg font-bold text-gray-900">{item.title || `Track #${item.track_id}`}</h3>
          <p className="text-sm text-gray-500">Track #{item.track_id} · submitted {formatDate(item.pending_audio?.submitted_at)}</p>
        </div>
      </div>
      <div className="grid md:grid-cols-2 gap-4 mt-4 text-sm">
        <div className="rounded-xl bg-gray-50 p-4">
          <p className="font-semibold text-gray-700">Current audio</p>
          <p className="mt-1 text-gray-900 break-all">{item.current_audio?.file_name || "—"}</p>
          <p className="text-gray-500">{seconds(item.current_audio?.duration)}</p>
          {item.current_audio?.url && <audio className="mt-2 w-full" controls preload="none" src={item.current_audio.url} />}
        </div>
        <div className="rounded-xl bg-[#f1f6f2] p-4">
          <p className="font-semibold" style={{ color: ACCENT }}>New audio</p>
          <p className="mt-1 text-gray-900 break-all">{item.pending_audio?.file_name || "—"}</p>
          <p className="text-gray-500">{seconds(item.pending_audio?.duration)}{item.pending_audio?.file_size ? ` · ${formatBytes(item.pending_audio.file_size)}` : ""}</p>
          {item.pending_audio?.url && <audio className="mt-2 w-full" controls preload="none" src={item.pending_audio.url} />}
        </div>
      </div>
      {rejecting && (
        <div className="mt-4">
          <label className="block text-sm font-semibold text-gray-700" htmlFor={`reason-${item.track_id}`}>Reason (shown to the artist)</label>
          <textarea id={`reason-${item.track_id}`} className="mt-1 w-full rounded-xl border border-gray-300 px-3 py-2 text-sm" rows={3} value={reason} disabled={busy} onChange={(e) => setReason(e.target.value)} data-testid="reject-reason" />
        </div>
      )}
      {error && <p className="mt-3 text-sm text-red-700" role="alert">{error}</p>}
      <div className="flex justify-end gap-3 mt-4">
        {rejecting ? (
          <>
            <SecondaryButton onClick={() => { setRejecting(false); setError(""); }} disabled={busy}>Back</SecondaryButton>
            <button type="button" onClick={() => review("reject")} disabled={busy} className="rounded-full font-semibold text-white px-7 py-3 bg-red-600 hover:bg-red-700 disabled:opacity-60" data-testid="reject-confirm">
              {busy ? "Working…" : "Confirm reject"}
            </button>
          </>
        ) : (
          <>
            <SecondaryButton onClick={() => setRejecting(true)} disabled={busy} data-testid="reject-start">Reject</SecondaryButton>
            <PrimaryButton onClick={() => review("approve")} disabled={busy} data-testid="approve">{busy ? "Working…" : "Approve"}</PrimaryButton>
          </>
        )}
      </div>
    </div>
  );
}

export default function AudioReplacementReview() {
  const [items, setItems] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(() => {
    setError("");
    trackApi.listAudioReplacements().then(
      (rows) => setItems(Array.isArray(rows) ? rows : []),
      (e) => {
        setError(e?.response?.status === 403 ? "Only moderators and admins can review audio replacements." : trackErrorMessage(e, MESSAGES[500]));
        setItems([]);
      }
    );
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <PageShell>
      <div className="max-w-4xl mx-auto px-4 py-8" data-testid="audio-replacement-review">
        <h1 className="text-2xl font-bold text-gray-900">Audio replacements awaiting review</h1>
        <p className="text-gray-600 mt-1">Approve to make the new file the track's audio (the earlier file is kept on record). Reject to keep the current audio and tell the artist why.</p>
        {error && <p className="mt-4 rounded-xl bg-red-50 border border-red-200 text-red-800 px-4 py-3 text-sm" role="alert">{error}</p>}
        <div className="mt-6 space-y-4">
          {items === null ? (
            <div className="flex justify-center py-16"><Loading /></div>
          ) : items.length === 0 && !error ? (
            <p className="rounded-2xl border border-gray-200 bg-white p-6 text-center text-gray-500" data-testid="replacement-empty">No audio replacements are waiting for review.</p>
          ) : (
            items.map((item) => <Row key={item.track_id} item={item} onDone={load} />)
          )}
        </div>
      </div>
    </PageShell>
  );
}
